import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppState } from '../../../client-redux/reducers';
import { LoginState } from '../../../client-redux/login/reducer';
import { Language } from '../../../languages/languageHandler';
import { Group, Loader, Table } from '@mantine/core';
import { getTicketStatus, getUrgencyType } from '../../utils';
import { fetchHelpdeskTickets, fetchTicketsXLS, HeldeskTicketsRequests } from '../../../../libraries/helpdesk/tickets';
import Header from '../../../components/util/header';
import PaginationComponent from '../../../components/pagination';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../../utils/date';
import { endOfDay } from 'date-fns';
import { useStatusOperDialog, isZarphSuccessResponse, isErrorResponse } from '../../../context/status-oper-dialog';
import { IconEdit } from '@tabler/icons-react';
import './styles.scss';

export enum TICKETSTATUS {
  ALL = 'all',
  OPEN = 'open',
  CLOSE = 'close'
}

const HelpdeskTicketList = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const controller = useRef(new AbortController()).current;
  const navigate = useNavigate();

  const [list, setList] = useState<HeldeskTicketsRequests[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 1,
    minHeightForDynamic: 400
  });
  const [totalRows, setTotalRows] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadTicketsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, offset, filterBarData]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };
  const mapTicketStatusToClosedParam = (status?: string) => {
    switch (status) {
      case TICKETSTATUS.OPEN:
        return 'false';
      case TICKETSTATUS.CLOSE:
        return 'true';
      default:
        return '';
    }
  };

  const loadTicketsList = () => {
    if (!login.token) return;

    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchHelpdeskTickets(
      controller,
      offset,
      limit,
      true,
      filterBarData?.ticketId,
      filterBarData?.eid,
      filterBarData?.entity,
      mapTicketStatusToClosedParam(filterBarData?.ticketStatus),
      formatDateToISO(filterBarData?.beginDateTime ?? undefined),
      formatDateToISO(filterBarData?.endDateTime)
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.tickets || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const getXLSReport = async () => {
    if (!filterBarData) return;

    try {
      const response = await fetchTicketsXLS(
        controller,
        filterBarData?.ticketId,
        filterBarData?.eid,
        filterBarData?.entity,
        mapTicketStatusToClosedParam(filterBarData?.ticketStatus),
        formatDateToISO(filterBarData?.beginDateTime ?? undefined),
        formatDateToISO(filterBarData?.endDateTime)
      );
      if (isErrorResponse(response) || response?.data.statusOper?.code !== 0) {
        handleApiError(response, lang.HELPDESK_TRANSACTIONS_XLS_ERROR);
        return;
      }
    } catch (err: any) {
      handleApiError(err, lang.HELPDESK_TRANSACTIONS_XLS_ERROR);
    }
  };

  const toggleNewTicket = () => {
    navigate(`/helpdesk/ticket/new/${filterBarData?.eid ? `?eid=${filterBarData.eid}` : ''}`);
  };

  return (
    <div className="helpdesk-tickets">
      <Header pageTitle={lang.GLOBAL_TICKETS} />

      <HelpdeskFilterBar
        onSearch={loadTicketsList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          ticketId: true,
          eid: true,
          entity: true,
          ticketStatus: true,
          beginDateTime: true,
          endDateTime: true
        }}
        initialData={{
          ticketStatus: TICKETSTATUS.OPEN,
          beginDateTime: undefined,
          endDateTime: endOfDay(new Date())
        }}
        xlsReport={getXLSReport}
        addButton={toggleNewTicket}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>{lang.GLOBAL_AGENT}</Table.Th>
              <Table.Th>{lang.GLOBAL_CLIENT}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_ID_MIN}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_SERIAL_NUMBER}</Table.Th>
              <Table.Th>{lang.GLOBAL_DATE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_COM_STATE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_COM_STATE}</Table.Th>
              <Table.Th>{lang.GLOBAL_TIME}</Table.Th>
              <Table.Th>{lang.GLOBAL_URGENCY}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {!list.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.map((ticket, index) => {
                    return (
                      <Table.Tr
                        key={index}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/helpdesk/ticket/read/${ticket.ticketID}`)}
                      >
                        <Table.Td>{ticket.ticketID}</Table.Td>
                        <Table.Td>{ticket.agent?.name}</Table.Td>
                        <Table.Td>{ticket.entityName}</Table.Td>
                        <Table.Td>{ticket.eid}</Table.Td>
                        <Table.Td>{ticket.sn}</Table.Td>
                        <Table.Td>{formatDateTimeNSecLabel(ticket.openDT, region)}</Table.Td>
                        <Table.Td className={ticket.status === 1 ? 'completed-ticket' : ''}>
                          {getTicketStatus(lang, ticket.status || 0).name}
                        </Table.Td>
                        <Table.Td>{formatDateTimeNSecLabel(ticket.statusDT, region)}</Table.Td>
                        <Table.Td>{ticket.time} min</Table.Td>
                        <Table.Td>{getUrgencyType(lang, ticket.urgency || 0)}</Table.Td>
                        <Table.Td>
                          <IconEdit
                            stroke={2}
                            style={{ cursor: 'pointer' }}
                            onClick={event => {
                              event.stopPropagation();
                              navigate(`/helpdesk/ticket/edit/${ticket.ticketID}`);
                            }}
                          />
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default HelpdeskTicketList;
